import {
  deleteBuilding,
  getAllBuildings,
} from "../../../Redux/Buildings/BuildingActions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import exportToExcel from "../../../Util/exportToExcel";
import { AppDispatch } from "../../../Redux/store";
import React, { useEffect, useState } from "react";
import { RootState } from "../../../Redux/store";
import { Link, useNavigate } from "react-router";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import styles from "./Buildings.module.scss";
import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

interface BuildingsType {
  address: string;
  amenigiesShare: number;
  amenitiesCount: number;
  buildingId: number;
  buildingName: string;
  buildingStatus: string;
  city: string;
  groupId: number;
  image: string;
  lat: number;
  long: number;
  ownerShare: number;
  password: string;
  terms: string;
  username: string;
  zip: string;
  state: string;
  groupName: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Buildings() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { buildings, buildingsLoading, deleteBuildingLoading } = useSelector(
    (state: RootState) => state.buildings,
  );
  const [rowData, setRowData] = useState<BuildingsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteBuildingId, setDeleteBuildingId] = useState<number>(-1);

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const columnDefs: ColDef<BuildingsType>[] = [
    {
      headerName: "Building Name",
      field: "buildingName",
      flex: 1, // changed from fixed width to flex
      minWidth: 150,
    },
    {
      headerName: "Location/Address",
      field: "address",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Group",
      field: "groupName",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Amenities",
      field: "amenitiesCount",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Status",
      field: "buildingStatus",
      flex: 1,
      minWidth: 100,
      cellClass: (params) => {
        return params?.data?.buildingStatus === "Active"
          ? "CompletedText"
          : params?.data?.buildingStatus === "Inactive"
            ? "pendingText"
            : "text-black-500";
      },
    },
    {
      sortable: false,
      suppressAutoSize: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      cellStyle: {
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className={styles.iconContainer}>
            <Menu
              direction="left"
              menuButton={
                <MenuButton>
                  <HiDotsVertical className={styles.icon} />
                </MenuButton>
              }
              transition
              portal
            >
              <MenuItem
                onClick={() =>
                  navigate(`/admin/buildings/edit/${params?.data?.buildingId}`)
                }
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setIsDeleteModalOpen(true);
                  setDeleteBuildingId(params?.data?.buildingId);
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const filteredRowData = rowData?.filter((row) => {
    return (
      row?.buildingName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      row?.address?.toLowerCase()?.includes(search?.toLowerCase())
    );
  });

  const deleteBuildingConfirmationHandler = () => {
    const successHandler = () => {
      setIsDeleteModalOpen(false);
      setDeleteBuildingId(-1);
    };
    dispatch(
      deleteBuilding({
        id: deleteBuildingId,
        successHandler,
      }),
    );
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllBuildings());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (buildings) {
      setRowData(buildings);
    }
  }, [buildings]);

  if (buildingsLoading) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onConfirm={deleteBuildingConfirmationHandler}
        heading="Delete Building"
        text="Are you sure you want to delete this building?"
        loading={deleteBuildingLoading}
      />
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Manage Buildings</h1>
          </div>
          <div className={styles.buttonContainer}>
            {filteredRowData?.length > 0 && (
              <button
                className={styles.button}
                onClick={() =>
                  exportToExcel({
                    data: filteredRowData || [],
                    fileName: "Buildings",
                  })
                }
              >
                Export
              </button>
            )}
            <Link to="/admin/buildings/add" className={styles.buttonContainer}>
              <button className={styles.button}>Add Building</button>
            </Link>
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.input}
              placeholder="Building"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
            />
            <div className={styles.iconContainer} onClick={() => setSearch("")}>
              <IoCloseSharp className={styles.icon} />
            </div>
          </div>

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default Buildings;
